import { useState } from 'react';

import BugReportSheet from '@/components/BugReportSheet';
import Toaster from '@/components/Toaster';
import { useTranslation } from '@/utils/i18n';
import { isBugReportAvailable } from '@/utils/sentry';

interface Props {
  /** Sentry event id of the crash; the user's report is linked to it. */
  eventId: string | null;
}

/**
 * Full-screen fallback after a render crash. Besides reloading, the user can
 * tell us what happened: the report lands on the crash's Sentry issue.
 */
export default function CrashFallback({ eventId }: Props) {
  const { t } = useTranslation();
  const [reportOpen, setReportOpen] = useState(false);
  const canReport = Boolean(eventId) && isBugReportAvailable();

  return (
    <div className="tw:flex tw:h-dvh tw:flex-col tw:items-center tw:justify-center tw:gap-4 tw:app-background tw:px-8 tw:text-center">
      <p className="tw:text-base tw:text-grey-2">{t('crash.message')}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="tw:rounded-2xl tw:bg-blue-primary tw:px-5 tw:py-3 tw:text-[15px] tw:font-semibold tw:text-white"
      >
        {t('crash.reload')}
      </button>
      {canReport && (
        <>
          <button
            type="button"
            data-testid="btn-crash-report"
            onClick={() => setReportOpen(true)}
            className="tw:px-5 tw:py-3 tw:text-[15px] tw:font-semibold tw:text-grey-3"
          >
            {t('crash.report')}
          </button>
          <BugReportSheet
            open={reportOpen}
            onClose={() => setReportOpen(false)}
            trigger="crash"
            associatedEventId={eventId ?? undefined}
            title={t('crash.report')}
            placeholder={t('crash.messagePlaceholder')}
          />
        </>
      )}
      {/* The app's Toaster is mounted under the router, which is gone here. */}
      <Toaster />
    </div>
  );
}
